import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import React, { useEffect, useRef, useState } from 'react';
import { Form, HiddenParam, SonForm, SubmitButton, useForm } from '../../../_lib/Form2';
import axios from 'axios';
import { LoadingSpinnerContainer } from '../../../components/atoms/LoadingSpinner';
import { StatusInfoError } from '../../../_lib/components/StatusInfo';
import { DownloadByteUnit, getFileExtensionDescription } from '../../../_lib/components/Download';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { useAppContext } from '../../../contexts/AppContext/AppContext';
import { FetchState } from '../../../hooks/reducers/useFetchReducer';
import { DownloadAbbr } from '../../../_lib/components/Download/partials/DownloadAbbr';
import { DD, DL, DT } from '../../../_lib/components/DefinitionList';
import { useSharedFilesSelectedItemsContext } from '../context/SharedFilesSelectedItems';
import { useDelayed } from '../../../hooks/useDelayed';

type SharedFilesGlobalDownload = {
  headline: string;
  closeModal: () => void;
  prepareMultiDownloadUrl: SonForm<any>;
};

type SharedFilesDownloadResponse = {
  totalFileSizeInBytes: string;
  totalFileCount: string;
  files: string[];
  directories: string[];
  form: SonForm<HiddenParam<'files'> | HiddenParam<'directories'> | HiddenParam<'archiveName'>>;
};

/**
 * Download Button for executing POST route to get archive.
 * Custom form with as our usual form ux does not fit here.
 */
function SharedFilesGlobalDownloadButton({
  form,
  closeModal,
}: {
  form: SharedFilesDownloadResponse['form'];
  closeModal: () => void;
}) {
  const { t } = useSonTranslation('SharedFiles');
  const { headMetaInfo } = useAppContext();
  const { formProps, params } = useForm({ form });
  const closeModalDelayed = useDelayed(closeModal);

  const [fetchState, setFetchState] = useState<FetchState<any, any>>({ type: 'initial' });

  const fileParams = params.getAllByName('files');
  const dirParams = params.getAllByName('directories');
  const archiveName = params.getByName('archiveName');
  return (
    <Form
      modifier="no-padding"
      action={formProps.action}
      method={formProps.method}
      onSubmit={async () => {
        // no prevent default here, we want to trigger browser download
        // we cant know if download started, so just close modal after a generous amount of time
        setFetchState({ type: 'loading' });
        closeModalDelayed();
      }}
    >
      {fileParams.map(file => (
        <input {...file} key={file.value} />
      ))}
      {dirParams.map(dir => (
        <input {...dir} key={dir.value} />
      ))}
      {headMetaInfo.title && <input {...archiveName} value={headMetaInfo.title} />}
      <SubmitButton
        fetchState={fetchState}
        messageSuccess=""
        messageFail=""
        messageSending={t('forms.globalDownload.downloadStarting')}
        buttonLabel={t('forms.globalDownload.buttonLabel')}
        disabled={fetchState.type === 'loading'}
      />
    </Form>
  );
}

/**
 * Modal for downloading multiple checked items.
 */
export function SharedFilesGlobalDownload({
  closeModal,
  headline,
  prepareMultiDownloadUrl,
}: SharedFilesGlobalDownload) {
  const { t } = useSonTranslation('SharedFiles');
  const { headMetaInfo } = useAppContext();
  const [response, setResponse] = useState<null | 'error' | SharedFilesDownloadResponse>(null);
  const { selectedFiles, selectedDirectories } = useSharedFilesSelectedItemsContext();

  // sentient flag for making sure call occurs only once as it is expensive
  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;

      axios
        .post(prepareMultiDownloadUrl.url, {
          files: selectedFiles,
          directories: selectedDirectories,
        })
        .then(result => setResponse(result.data))
        .catch(() => setResponse('error'));
    }
  }, [prepareMultiDownloadUrl.url, selectedDirectories, selectedFiles]);

  return (
    <ModalDialog closeModal={closeModal} headline={headline} initialFocusIndex={2}>
      <div className="shared-files">
        <div aria-live="polite">
          {response === null && (
            <>
              <p className="shared-files__download-description font-regular-15-19">
                {t('forms.globalDownload.preparingDownload')}
              </p>
              <LoadingSpinnerContainer />
            </>
          )}
          {response === 'error' && <StatusInfoError message={t('forms.globalDownload.error')} />}
        </div>
        {response !== null && response !== 'error' && (
          <>
            <p className="shared-files__download-description font-regular-15-19">
              {t('forms.globalDownload.helpText')}
            </p>
            <DL>
              <DT>{t('forms.globalDownload.file.fileName')}:</DT>
              <DD>{headMetaInfo.title || 'Archive'}</DD>
              <DT>{t('forms.globalDownload.file.fileType')}:</DT>
              <DD>
                <p>
                  <DownloadAbbr text="zip" title={getFileExtensionDescription('zip')} />
                </p>
              </DD>
              <DT>{t('forms.globalDownload.file.fileSize')}:</DT>
              <DD>
                <p>
                  <DownloadByteUnit bytes={Number(response.totalFileSizeInBytes)} />
                </p>
              </DD>
            </DL>
            <SharedFilesGlobalDownloadButton form={response.form} closeModal={closeModal} />
          </>
        )}
      </div>
    </ModalDialog>
  );
}
